import {
  BasketIcon,
  BigDotIcon,
  Button,
  CheckIcon,
  debounce,
  DocumentIcon,
  Down1Icon,
  DownloadIcon,
  FilterIcon,
  GKButton,
  GKTextField,
  Grid2,
  HashIcon,
  IconButton,
  KycIcon,
  Loader,
  NoData,
  RightIcon,
  RiskProfileIcon,
  RupeeIcon,
  SearchIcon,
  SendIcon,
  SheildIcon,
  Show,
  StatusField,
  Typography,
  Up1Icon,
  VerifySolidIcon,
} from "components";
import { AnimatePresence, motion } from "framer-motion";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import {
  OnboardingDashboardInterface,
  OnboardingSingleClient,
} from "interfaces/ClientOnboarding";
import { filter, isEmpty, map, orderBy } from "lodash";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { matchDate, refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import useDocumentTitle from "utils/useDocumentTitle";
import ClientCustomAgreement from "./ClientCustomAgreement";
import KYCModal from "./KYCModal";
import OnboardingSorting from "./OnboardningSorting";
import SendLinkModal from "./SendLinkModal";

interface SummaryStatus {
  name: string;
  date: string | null;
  icon: React.ElementType;
  status: string | null;
  Id?: number;
}

interface APIResponseInterface {
  rowId: number;
  data: { [key: string]: boolean };
}

const CompliancePage = () => {
  useDocumentTitle("Onboarding");

  const [selectedClient, setSelectedClient] =
    useState<OnboardingSingleClient>();

  const [selectedPortfolio, setSelectedPortfolio] = useState<number | null>(
    null,
  );

  const [allClients, setAllClients] = useState<any[]>([]);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const [FilterPopover, setFilterPopover] = useState(false);

  const [customAgreement, setCustomAgreement] = useState(false);

  const [filteredClients, setFilteredClients] = useState<string>("");

  const [agreementDetails, setAgreementDetails] = useState({
    id: null,
    type: null,
  });

  const [selectedProofItem, setSelectedProofItem] = useState<any>(null);

  const [selectedStage, setSelectedStage] = useState<string>("");

  const [openKYCModal, setOpenKYCModal] = useState(false);

  const [sortType, setSortType] = useState({
    sortBy: "clientName",
    order: "asc",
    filter: "",
  });

  const [existPortfolio, setExistPortfolio] = useState(false);

  const { isFetching } = useFetchQuery<OnboardingDashboardInterface>({
    key: [QueryKeys.COMPLIANCE_LIST],
    route: `/client/onboarding/`,
    success: (data: any) => {
      const clients = data.data.data.map((client: any) => ({
        ...client,
        riskprofileId: client.clientDetails?.riskprofileId,
      }));
      setAllClients(clients);
    },
  });

  const { mutate: handleKycDownload } = useMutation({
    mutationKey: ["DOWNLOAD_KYC"],
    mutationFn: (kycId: string) =>
      axiosInstance.get(`/download-kyc-doc/${kycId}/`, {}),
  });

  const { mutate: handleKraDownload, isLoading: kraDownloadLoading } =
    useMutation({
      mutationKey: ["DOWNLOAD_KRA"],
      mutationFn: (pan: string) =>
        axiosInstance.get(`/kra-details/download-docs/?pan=${pan}`, {
          responseType: "blob",
        }),
      onSuccess: (response, pan) => {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const fileName = `KRA-${pan}.pdf`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("KRA downloaded successfully.");
      },
    });

  const { mutate: handleRiskProfileDownload, isLoading: riskLoading } =
    useMutation({
      mutationKey: ["DOWNLOAD_RISK_PROFILE"],
      mutationFn: (riskprofileId: number) =>
        axiosInstance.get(`/download-risk-doc/${riskprofileId}/`, {
          responseType: "blob",
        }),
      onSuccess: (response, riskprofileId) => {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const fileName = `risk-profile-${selectedClient?.clientName}-${riskprofileId}.pdf`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Risk profile downloaded successfully.");
      },
      onError: () => {
        toast.error("Failed to download risk profile.");
      },
    });

  const { mutate: handleMarkAsComplete } = useMutation({
    mutationKey: ["MARK_OVERALL_STATUS_COMPLETE"],
    mutationFn: (id: any) =>
      axiosInstance.post(`/portfolios/${id}/onboarding/`),
    onSuccess: () => {
      refetchQuery(QueryKeys.COMPLIANCE_LIST);
    },
  });

  const { mutate: handleKycRequest, isLoading: kycRequested } = useMutation({
    mutationKey: ["KYC_REQUEST"],
    mutationFn: ({ clientId, kycId }: { clientId: number; kycId: number }) =>
      axiosInstance.put(`/kyc/${clientId}/${kycId}/`),
    onSuccess: () => {
      refetchQuery(QueryKeys.COMPLIANCE_LIST);
      toast.success("KYC requested successfully!");
    },
    onError: () => {
      toast.error("Failed to request KYC");
    },
  });

  const { mutate, isLoading: riskRequestLoading } = useMutation({
    mutationFn: (data: { type: "portfolio" | "client"; id: number }) => {
      const payload =
        data.type === "portfolio"
          ? { portfolio: data.id }
          : { client: data.id };
      return axiosInstance.post(`/riskprofile-request/`, payload);
    },
    mutationKey: ["RISK_PROFILE_CLIENT_REQUEST"],
    onSuccess: () => {
      toast.success("Questionnaire requested successfully.");
      // refetchQuery(QueryKeys.COMPLIANCE_LIST);
    },
  });

  const { mutate: handleResendAgreement, isLoading: resendAgreementLoading } =
    useMutation({
      mutationKey: ["RESEND_AGREEMENT"],
      mutationFn: (id: any) =>
        axiosInstance.post(
          `/resendAgreement/resendAgreement/?agreement_id=${id}`,
        ),
      onSuccess: () => {
        toast.success("Agreement resent successfully.");
        refetchQuery(QueryKeys.COMPLIANCE_LIST);
      },
    });

  const { mutate: handleProof, isLoading: proofLoading } = useMutation({
    mutationKey: ["ACCEPT_REJECT_PROOF"],
    mutationFn: (data: APIResponseInterface) =>
      axiosInstance.patch(`/client/${data?.rowId}/proofs/`, data.data),
    onSuccess: () => {
      refetchQuery(QueryKeys.COMPLIANCE_LIST);
    },
  });

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilteredClients(value);
      }, 500),
    [allClients],
  );

  const handleSend = ({
    dataItem,
    isPortfolio,
    item,
  }: {
    dataItem: SummaryStatus;
    isPortfolio: boolean;
    item: any;
  }) => {
    setExistPortfolio(isPortfolio);

    if (
      dataItem?.name === "Risk Profile" &&
      dataItem?.status?.toLowerCase() === "pending"
    ) {
      setSelectedProofItem(item);
      setSelectedStage(dataItem.name);
      setOpenModal(true);
    }

    if (dataItem?.name === "KRA/KYC") {
      const status = dataItem?.status?.toLowerCase();

      if (status === "pending") {
        setSelectedProofItem(item);
        setSelectedStage(dataItem.name);
        setOpenKYCModal(true);
      }
      if (status === "requested") {
        // Get clientId and kycId
        const clientId = selectedClient?.id;
        const kycId = item?.kycId; // from portfolioDetails

        if (clientId && kycId) {
          handleKycRequest({ clientId, kycId });
        }
      }
    }

    if (dataItem?.name === "Agreement") {
      const status = dataItem?.status?.toLowerCase();
      if (status === "pending") {
        setAgreementDetails({
          id: item?.id,
          type: isPortfolio ? "portfolio" : "client",
        });
        setCustomAgreement(true);
      }
      if (status === "requested") {
        handleResendAgreement(item?.agreementId || item?.id);
      }
    }

    if (
      dataItem?.name === "Investment Proof" &&
      (dataItem?.status === "pending" || dataItem?.status === "false")
    ) {
      // handleProof({
      // rowId: item?.id,
      // data: { both: true },
      // });
      setSelectedProofItem(item);
      setSelectedStage(dataItem.name);
      setOpenModal(true);
    }
  };

  if (isFetching) return <Loader />;

  return (
    <>
      <Show.When isTrue={!allClients?.length}>
        <NoData
          customImg={
            <img
              loading="lazy"
              src="/LandingPage/client.svg"
              alt=""
              draggable={false}
            />
          }
          text="No Clients Available for Onboarding"
          subText="Start by adding a client in the “Clients” tab — once added, their onboarding steps will appear here."
        />
      </Show.When>

      <Show.When isTrue={!!allClients?.length}>
        <Grid2 container>
          <Grid2
            size={4}
            borderRight="solid 1px var(--neutral-100)"
            borderBottom="solid 1px var(--neutral-100)"
            px={2}
            py={1}
            bgcolor="var(--neutral-bg-100)"
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <GKTextField
                size="small"
                onChange={(event) => {
                  debouncedSearch(event.target.value);
                }}
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon style={{ marginRight: 4 }} />,
                    placeholder: "Search client name and id...",
                    style: {
                      background: "#fff",
                    },
                  },
                }}
              />
              <IconButton
                onClick={() => {
                  setFilterPopover(!FilterPopover);
                }}
              >
                <FilterIcon />
              </IconButton>
            </div>
          </Grid2>
          <Grid2
            size={8}
            px={3}
            py={1}
            borderBottom={
              Boolean(selectedClient?.id) && `solid 1px var(--neutral-100)`
            }
            display={"flex"}
            alignItems={"center"}
          >
            <Show.When isTrue={Boolean(selectedClient?.id)}>
              <Typography color="var(--grey-900)" fontSize={16}>
                {selectedClient?.clientName}
              </Typography>
              <BigDotIcon color="var(--neutral-100)" />
              <Typography color="var(--neutral-500)">
                {selectedClient?.clientCode}
              </Typography>
              <BigDotIcon color="var(--neutral-100)" fontSize={12} />
              {selectedClient?.kycStatus?.toLowerCase() === "completed" ||
              selectedClient?.kycStatus?.toLowerCase() === "approved" ? (
                <Typography
                  display={"flex"}
                  alignItems={"center"}
                  gap={0.5}
                  color="var(--success-700)"
                >
                  <VerifySolidIcon />
                  KYC
                </Typography>
              ) : (
                <StatusField
                  styles={{ textTransform: "uppercase" }}
                  statusString={
                    selectedClient?.kycStatus?.toLowerCase() === "pending"
                      ? "KYC Pending"
                      : selectedClient?.kycStatus?.toLowerCase() === "requested"
                        ? "KYC Requested"
                        : selectedClient?.kycStatus?.toLowerCase() ===
                            "rejected"
                          ? "KYC Rejected"
                          : "KYC"
                  }
                />
              )}
              {selectedClient?.portfolioDetails?.length > 0 && (
                <Typography color="var(--neutral-500)" ml="auto">
                  Total Portfolio
                  {selectedClient?.portfolioDetails?.length > 1
                    ? "s"
                    : ""}:{" "}
                  <span style={{ color: "var(--neutral-700)" }}>
                    {selectedClient?.portfolioDetails?.length}
                  </span>
                </Typography>
              )}
            </Show.When>
          </Grid2>
          <Grid2
            size={4}
            borderRight={`solid 1px var(--neutral-100)`}
            maxHeight={"calc(100vh - 190px)"}
            overflow={"auto"}
          >
            <AnimatePresence>
              {FilterPopover && (
                <motion.div
                  initial={{ opacity: 0, height: 0, overflow: "hidden" }}
                  animate={{
                    opacity: 1,
                    height: "auto",
                  }}
                  exit={{
                    opacity: 0,
                    height: 0,
                  }}
                >
                  <Grid2
                    container
                    size={12}
                    px={2}
                    py={1}
                    alignItems="center"
                    borderBottom="solid 1px var(--neutral-100)"
                  >
                    <Grid2 size={12}>
                      <OnboardingSorting
                        setSortType={setSortType}
                        sortType={sortType}
                      />
                    </Grid2>
                  </Grid2>
                </motion.div>
              )}
            </AnimatePresence>
            <Grid2
              container
              size={12}
              sx={{
                p: 1,
                position: "sticky",
                top: 0,
                backgroundColor: "var(--white)",
                zIndex: 10,
                borderBottom: "1px solid var(--neutral-100)",
              }}
              alignItems="center"
            >
              <Grid2>
                <Typography
                  fontSize={10}
                  fontWeight={600}
                  color="var(--grey-500)"
                >
                  Total Clients:
                </Typography>
              </Grid2>
              <Grid2 ml={0.5}>
                <Typography
                  fontSize={10}
                  fontWeight={600}
                  color="var(--grey-700)"
                >
                  {sortType?.filter
                    ? allClients?.filter(
                        (client) => client?.overallStatus === sortType.filter,
                      )?.length || 0
                    : allClients?.length || 0}
                </Typography>
              </Grid2>
            </Grid2>

            {map(
              orderBy(
                filter(
                  filter(allClients, (filterData) =>
                    isEmpty(filteredClients)
                      ? filterData?.clientName
                      : filterData.clientName
                          .toLowerCase()
                          .includes(filteredClients.toLowerCase()),
                  ),
                  (filterData) =>
                    isEmpty(sortType?.filter)
                      ? filterData?.overallStatus
                      : filterData?.overallStatus === sortType?.filter,
                ),
                [sortType?.sortBy],
                [sortType?.order as any],
              ),
              (dataItem, index) => {
                return (
                  <Button
                    key={index}
                    onClick={() => {
                      setSelectedClient(dataItem);
                    }}
                    fullWidth
                    disableRipple
                    sx={{
                      all: "unset",
                      width: "100%",
                      bgcolor:
                        selectedClient?.id === dataItem?.id
                          ? "var(--neutral-bg-100)"
                          : "",
                      "&:hover": {
                        cursor: "pointer",
                        bgcolor: "var(--grey-bg-100)",
                      },
                    }}
                  >
                    <Grid2
                      container
                      spacing={1}
                      px={3}
                      py={2}
                      width={"100%"}
                      borderBottom={`solid 1px var(--neutral-100)`}
                    >
                      <Grid2 size={"auto"} textAlign={"left"}>
                        <Typography color="var(--grey-900)">
                          {dataItem?.clientName}
                        </Typography>
                        <Typography color="var(--neutral-500)" fontSize={12}>
                          {dataItem?.clientCode}
                        </Typography>
                      </Grid2>
                      <Grid2 size={"auto"} textAlign={"left"}>
                        {dataItem?.overallStatus?.toLowerCase() ===
                        "completed" ? (
                          <Typography
                            display={"flex"}
                            alignItems={"center"}
                            gap={0.5}
                            color="var(--success-700)"
                          >
                            <VerifySolidIcon />
                            Completed
                          </Typography>
                        ) : (
                          <StatusField
                            styles={{ textTransform: "uppercase" }}
                            statusString={dataItem?.overallStatus || ""}
                          />
                        )}
                      </Grid2>
                      <Grid2
                        size={"auto"}
                        textAlign={"right"}
                        ml={"auto"}
                        display={"flex"}
                        alignItems={"center"}
                        gap={2}
                      >
                        {Number(dataItem?.pendingStatusCount) > 0 && (
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="var(--black-white-white, #FFF)"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 25,
                              height: 25,
                              backgroundColor: "#C35245",
                              borderRadius: "50%",
                            }}
                          >
                            {dataItem?.pendingStatusCount}
                          </Typography>
                        )}
                        <RightIcon />
                      </Grid2>
                    </Grid2>
                  </Button>
                );
              },
            )}
          </Grid2>
          <Grid2 size={8} p={3}>
            <Show.When isTrue={!selectedClient?.id}>
              <NoData
                customImg={
                  <img
                    loading="lazy"
                    alt=""
                    src="/LandingPage/client.svg"
                    draggable={"false"}
                  />
                }
                text="Select a Client to Get Started"
                subText="Pick a client from the left panel to see their portfolio onboarding journey."
              />
            </Show.When>
            <Show.When
              isTrue={
                Boolean(selectedClient) &&
                Boolean(selectedClient?.id) &&
                selectedClient?.portfolioDetails !== null
              }
            >
              <Grid2
                container
                spacing={2}
                maxHeight={"calc(100vh - 222px)"}
                overflow={"auto"}
              >
                {(selectedClient?.portfolioDetails?.length > 0
                  ? selectedClient?.portfolioDetails
                  : [selectedClient?.clientDetails]
                )?.map((item, i) => {
                  const isPortfolio = !!item?.portfolioCode && !!item?.id;
                  const isExpanded = isPortfolio
                    ? selectedPortfolio === i
                    : true;
                  const showMarkButton = isPortfolio
                    ? selectedPortfolio === i
                    : true;

                  const summaryStatus = [
                    {
                      name: "KRA/KYC",
                      date: item?.kraDate,
                      icon: KycIcon,
                      status: item?.kraStatus,
                      Id: item?.id,
                      icon1: CheckIcon,
                    },
                    {
                      name: "Risk Profile",
                      date: item?.riskprofileDate,
                      icon: RiskProfileIcon,
                      status: item?.riskStatus,
                      Id: item?.id,
                      icon1: CheckIcon,
                    },
                    {
                      name: "Agreement",
                      date: item?.agreementDate,
                      icon: SheildIcon,
                      status: item?.agreementStatus,
                      doc: item?.agreementDoc,
                      icon1: CheckIcon,
                    },
                    {
                      name: "Investment Proof",
                      date: item?.proofDate,
                      icon: DocumentIcon,
                      status:
                        item?.proofStatus === true
                          ? "completed"
                          : item?.proofStatus === false
                            ? "Pending"
                            : item?.proofStatus,
                      doc: item?.investmentProof,
                      icon1: CheckIcon,
                    },
                    {
                      name: "Payment",
                      date: item?.paymentDate,
                      icon: RupeeIcon,
                      status:
                        item?.paymentStatus === true ||
                        item?.paymentStatus === "completed"
                          ? "completed"
                          : "pending",
                      icon1: CheckIcon,
                    },
                  ];

                  return (
                    <Grid2
                      key={i}
                      p={2}
                      size={12}
                      container
                      spacing={0}
                      borderRadius={2}
                      border="solid 1px var(--neutral-100)"
                    >
                      {isPortfolio && (
                        <>
                          <Grid2 size={12} container spacing={1}>
                            <Grid2
                              size="auto"
                              fontWeight={600}
                              color={"var(--grey-900)"}
                            >
                              {item?.name}
                            </Grid2>
                            <Grid2 size="auto">
                              <BigDotIcon
                                color={"var(--neutral-100)"}
                                width={20}
                                height={20}
                              />
                            </Grid2>
                            <Grid2 size="auto">
                              <Typography color={"var(--neutral-500)"}>
                                Portfolio Code:
                              </Typography>
                            </Grid2>
                            <Grid2 size="auto">{item?.portfolioCode}</Grid2>
                            <Grid2 size="auto" ml="auto">
                              <StatusField statusString={item?.overallStatus} />
                            </Grid2>
                          </Grid2>

                          <Grid2
                            size={12}
                            display="flex"
                            alignItems="center"
                            mt={2}
                            gap={1}
                          >
                            <BasketIcon color={"var(--neutral-500)"} />
                            <Typography color={"var(--neutral-500)"}>
                              Basket Name:
                            </Typography>
                            <Typography color={"var(--neutral-700)"}>
                              {item?.basketName || "-"}
                            </Typography>
                          </Grid2>

                          <Grid2
                            size={12}
                            display="flex"
                            alignItems="center"
                            mb={2}
                            gap={1}
                          >
                            <HashIcon color={"var(--neutral-500)"} />
                            <Typography color={"var(--neutral-500)"}>
                              Basket Code:
                            </Typography>
                            <Typography color={"var(--neutral-700)"}>
                              {item?.basketCode || "-"}
                            </Typography>
                          </Grid2>
                        </>
                      )}
                      <Show.When isTrue={isExpanded}>
                        <Grid2
                          my={2}
                          size={12}
                          borderBottom="solid 1px var(--neutral-100)"
                          borderTop={
                            isPortfolio
                              ? "solid 1px var(--neutral-100)"
                              : "none"
                          }
                          mt={isPortfolio ? 2 : "none"}
                        >
                          <Typography
                            fontSize={12}
                            fontWeight={600}
                            color={"var(--neutral-colors-neutral-500, #697586)"}
                            sx={{ mt: isPortfolio ? 2 : "none" }}
                          >
                            STATUS SUMMARY:
                          </Typography>
                          {summaryStatus.map((dataItem) => {
                            return (
                              <Grid2
                                key={dataItem.name}
                                container
                                size={12}
                                py={1.5}
                                px={0}
                                borderBottom="solid 1px var(--neutral-100)"
                                alignItems="center"
                              >
                                <Grid2 size="auto" flex={1}>
                                  <GKButton
                                    key={dataItem.name}
                                    variant="text"
                                    size="extraSmall"
                                    fullWidth
                                    sx={{
                                      justifyContent: "flex-start",
                                      textAlign: "left",
                                      p: 0,
                                      borderRadius: 0,
                                    }}
                                    onClick={() => {
                                      let complianceTabValue = 0;

                                      switch (dataItem.name) {
                                        case "KRA/KYC":
                                          complianceTabValue = 1;
                                          break;
                                        case "Agreement":
                                          complianceTabValue = 2;
                                          break;
                                        case "Risk Profile":
                                          complianceTabValue = 3;
                                          break;
                                        case "Investment Proof":
                                          complianceTabValue = 4;
                                          break;
                                        case "Payment":
                                          complianceTabValue = 5;
                                          break;
                                        default:
                                          complianceTabValue = 0;
                                      }

                                      dispatch(
                                        TAB({
                                          tabKey: "clientERMPageTabs",
                                          tabValue: 4,
                                        }),
                                      );
                                      dispatch(
                                        TAB({
                                          tabKey: "complianceTabs",
                                          tabValue: complianceTabValue,
                                        }),
                                      );

                                      navigate(
                                        `/app/clients/${selectedClient.id}`,
                                        {
                                          state: {
                                            name: selectedClient.clientName,
                                          },
                                        },
                                      );
                                    }}
                                  >
                                    <Grid2 container alignItems="center">
                                      <Grid2
                                        size="auto"
                                        display="flex"
                                        alignItems="center"
                                        mr={1}
                                        bgcolor="var(--neutral-50)"
                                        height={40}
                                        width={40}
                                        justifyContent="center"
                                        borderRadius={50}
                                      >
                                        {dataItem?.status === "completed" ||
                                        dataItem?.status === "approved" ? (
                                          <dataItem.icon1
                                            width={20}
                                            height={20}
                                            color="var(--success-600)"
                                          />
                                        ) : (
                                          <dataItem.icon
                                            width={20}
                                            height={20}
                                            color="var(--neutral-500)"
                                          />
                                        )}
                                      </Grid2>
                                      <Grid2 size="auto" mr={5}>
                                        <Typography
                                          fontSize={12}
                                          fontWeight={600}
                                          color="var(--grey-900)"
                                          sx={{ width: 235 }}
                                        >
                                          {dataItem?.name}
                                        </Typography>

                                        <Typography color="var(--neutral-500)">
                                          Last Activity:{" "}
                                          <span
                                            style={{
                                              color: "var(--neutral-700)",
                                            }}
                                          >
                                            {dataItem?.date
                                              ? matchDate(dataItem.date)
                                              : "–"}
                                          </span>
                                        </Typography>
                                      </Grid2>
                                      <Grid2 size="auto">
                                        <StatusField
                                          statusString={
                                            dataItem?.status || "pending"
                                          }
                                        />
                                      </Grid2>
                                    </Grid2>
                                  </GKButton>
                                </Grid2>
                                <Grid2
                                  size="auto"
                                  ml="auto"
                                  display="flex"
                                  alignItems="center"
                                >
                                  {dataItem?.name !== "Payment" && (
                                    <>
                                      {dataItem?.status === "completed" ||
                                      dataItem?.status === "Moderate" ||
                                      dataItem?.status === "Low" ||
                                      dataItem?.status === "approved" ? (
                                        <IconButton
                                          loading={
                                            (dataItem?.name ===
                                              "Risk Profile" &&
                                              riskLoading) ||
                                            (dataItem?.name === "KRA/KYC" &&
                                              kraDownloadLoading)
                                          }
                                          onClick={() => {
                                            if (dataItem?.name === "KRA/KYC") {
                                              const clientDetails =
                                                selectedClient?.clientDetails;
                                              const portfolioItem = item;

                                              const idType =
                                                clientDetails?.identificationType ||
                                                portfolioItem?.identificationType;

                                              if (idType === "KYC") {
                                                const kycId =
                                                  clientDetails?.kycId ||
                                                  portfolioItem?.kycId;
                                                if (kycId) {
                                                  handleKycDownload(
                                                    String(kycId),
                                                  );
                                                }
                                              } else if (idType === "KRA") {
                                                const pan =
                                                  clientDetails?.pan ||
                                                  portfolioItem?.pan ||
                                                  "";
                                                handleKraDownload(pan);
                                              }
                                            }

                                            if (
                                              dataItem?.name === "Risk Profile"
                                            ) {
                                              handleRiskProfileDownload(
                                                item?.riskprofileId,
                                              );
                                            }
                                            if (
                                              dataItem?.name === "Agreement" &&
                                              dataItem?.doc
                                            ) {
                                              fetch(dataItem.doc)
                                                .then((response) =>
                                                  response.blob(),
                                                )
                                                .then((blob) => {
                                                  const url =
                                                    window.URL.createObjectURL(
                                                      blob,
                                                    );
                                                  const link =
                                                    document.createElement("a");
                                                  link.href = url;
                                                  link.download = dataItem.doc
                                                    .split("/")
                                                    .pop();
                                                  document.body.appendChild(
                                                    link,
                                                  );
                                                  link.click();
                                                  link.remove();
                                                  window.URL.revokeObjectURL(
                                                    url,
                                                  );
                                                })
                                                .catch((error) =>
                                                  console.error(
                                                    "Download failed:",
                                                    error,
                                                  ),
                                                );
                                            }

                                            if (
                                              dataItem?.name ===
                                              "Investment Proof"
                                            ) {
                                              window.open(
                                                dataItem?.doc,
                                                "_blank",
                                              );
                                            }
                                          }}
                                        >
                                          <DownloadIcon />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          loading={
                                            (dataItem?.name ===
                                              "Investment Proof" &&
                                              proofLoading) ||
                                            (dataItem?.name ===
                                              "Risk Profile" &&
                                              riskRequestLoading) ||
                                            (dataItem?.name === "Agreement" &&
                                              resendAgreementLoading) ||
                                            (dataItem?.name === "KRA/KYC" &&
                                              kycRequested)
                                          }
                                          onClick={() =>
                                            handleSend({
                                              dataItem,
                                              isPortfolio,
                                              item,
                                            })
                                          }
                                          disabled={
                                            !isPortfolio &&
                                            (dataItem?.name ===
                                              "Investment Proof" ||
                                              dataItem?.name === "Payment")
                                          }
                                        >
                                          <SendIcon />
                                        </IconButton>
                                      )}
                                    </>
                                  )}
                                </Grid2>
                              </Grid2>
                            );
                          })}
                        </Grid2>
                      </Show.When>
                      <Grid2
                        size={12}
                        display="flex"
                        justifyContent={
                          isPortfolio ? "space-between" : "flex-end"
                        }
                        alignItems="center"
                      >
                        {isPortfolio && (
                          <Button
                            sx={{ p: 0 }}
                            endIcon={
                              selectedPortfolio === i ? (
                                <Up1Icon />
                              ) : (
                                <Down1Icon />
                              )
                            }
                            onClick={() =>
                              setSelectedPortfolio(
                                selectedPortfolio === i ? null : i,
                              )
                            }
                            // onClick={() => setOpenModal(true)}
                          >
                            {selectedPortfolio === i
                              ? "Show less details"
                              : "Show more details"}
                          </Button>
                        )}
                        {showMarkButton && isPortfolio && (
                          <Button
                            startIcon={<CheckIcon />}
                            variant="contained"
                            disabled={
                              item?.overallStatus?.toLowerCase() === "completed"
                            }
                            onClick={() => handleMarkAsComplete(item.id)}
                          >
                            Mark as Complete
                          </Button>
                        )}
                      </Grid2>
                    </Grid2>
                  );
                })}
              </Grid2>
            </Show.When>
            <Show.When isTrue={customAgreement}>
              <ClientCustomAgreement
                setOpen={setCustomAgreement}
                open={customAgreement}
                {...agreementDetails}
              />
            </Show.When>

            {openKYCModal && (
              <KYCModal
                open={openKYCModal}
                setOpen={setOpenKYCModal}
                clientData={selectedClient}
                portfolioItem={selectedProofItem}
                isPortfolio={existPortfolio}
              />
            )}

            {openModal && selectedProofItem && (
              <SendLinkModal
                open={openModal}
                setOpen={setOpenModal}
                stageName={selectedStage}
                email={
                  selectedProofItem?.email ||
                  selectedClient?.clientDetails?.email ||
                  ""
                }
                onSend={() => {
                  if (selectedStage === "Risk Profile") {
                    mutate({
                      type: selectedProofItem?.portfolioCode
                        ? "portfolio"
                        : "client",
                      id: selectedProofItem.id,
                    });
                  } else if (selectedStage === "Investment Proof") {
                    handleProof({
                      rowId: selectedProofItem.id,
                      data: { both: true },
                    });
                  }
                  setOpenModal(false);
                }}
              />
            )}
          </Grid2>
        </Grid2>
      </Show.When>
    </>
  );
};

export default CompliancePage;
